import React, {
	useRef,
	type ReactNode,
	useCallback,
	useEffect,
	createContext,
	useContext,
} from 'react';
import isEqual from 'lodash/isEqual';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
// @ts-expect-error - TS2305 - Module '"relay-runtime"' has no exported member 'Query'.
import type { Query as RelayQuery, Variables, FetchPolicy } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
import { ErrorBoundary } from './error-boundary/index.tsx';
import { Query } from './query/index.tsx';
import { QueriesContainer, useGetQueries, useGetQueryById } from './store/index.tsx';
import { Suspense } from './suspense/index.tsx';

const QueryRenderer = ({ id }: { id: string }) => {
	const [queryConfig] = useGetQueryById(id);

	if (queryConfig === undefined) {
		throw new Error(`Query config not found for id=${id}`);
	}

	const { skip } = queryConfig;

	if (skip) return null;

	return (
		<Suspense id={id}>
			<ErrorBoundary id={id}>
				<Query id={id} />
			</ErrorBoundary>
		</Suspense>
	);
};

const Queries = () => {
	const [queries] = useGetQueries();

	return (
		<>
			{queries.map(({ id }) => (
				<QueryRenderer key={id} id={id} />
			))}
		</>
	);
};

// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
const RelayMigrationContext = createContext();

export const RelayMigrationProvider = ({ children }: { children: ReactNode }) => (
	<QueriesContainer>
		<RelayMigrationContext.Provider value>
			{children}
			<Queries />
		</RelayMigrationContext.Provider>
	</QueriesContainer>
);

export type QueryResult<TVariables extends Variables, TData> = {
	data: TData | null;
	error: Error | typeof undefined;
	loading: boolean;
	refetch: (variables?: TVariables) => Promise<void>;
};

export const useQuery = <TVariables extends Variables, TData, TRawResponse = undefined>(
	query: RelayQuery<TVariables, TData, TRawResponse>,
	options: {
		variables?: TVariables;
		skip?: boolean;
		fetchPolicy?: FetchPolicy | typeof undefined;
	} = {},
): QueryResult<TVariables, TData> => {
	// Note: It's not safe to default variables to `{}`, as that doesn't
	// necessary match the variables type expected by the query
	const { skip = false, variables = {}, fetchPolicy } = options; // TODO: add support for errorPolicy, etc

	const isProviderInitialized = useContext(RelayMigrationContext);

	if (isProviderInitialized !== true) {
		throw new Error(
			`Look like you forgot to wrap package root component with

            import { RelayMigrationProvider } from './index.tsx';

            <RelayMigrationProvider>
                <MyApp>
            </RelayMigrationProvider>`,
		);
	}

	const env = useRelayEnvironment();
	const id = useRef(`query-key-${uuid()}`).current;
	const [queryConfig, { registerOrUpdateQuery, removeQuery, forceCacheRecheck }] =
		useGetQueryById(id);

	const prevProps = useRef<unknown>();

	const refetch = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'newVariables' implicitly has an 'any' type.
		(newVariables) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
			const vars: TVariables = newVariables || (variables as any as TVariables);

			// @ts-expect-error - TS2344 - Type 'Variables' is missing the following properties from type 'OperationType': variables, response
			const promise = fetchQuery<TVariables>(env, query, vars, {
				fetchPolicy: 'network-only',
			}).toPromise();

			forceCacheRecheck({
				query,
				variables: vars,
			});

			return promise;
		},
		[env, query, variables, forceCacheRecheck],
	);

	useEffect(() => {
		// deepEqual check in case object reference changed but key/values are the same.
		// For example: variables
		if (isEqual(prevProps.current, { skip, query, variables, fetchPolicy })) return;
		prevProps.current = { skip, query, variables, fetchPolicy };

		registerOrUpdateQuery({
			id,
			query,
			variables,
			fetchPolicy,
			skip,
		});
	}, [id, registerOrUpdateQuery, removeQuery, skip, query, variables, fetchPolicy]);

	// cleap up query after useQuery unmount
	// @ts-expect-error - TS2322 - Type '() => void | Promise<void>' is not assignable to type 'void | Destructor'.
	useEffect(() => () => removeQuery(id), [removeQuery, id]);

	const result = queryConfig
		? { ...queryConfig.result, refetch }
		: { loading: true, data: null, error: undefined, refetch: () => Promise.resolve() };

	// @ts-expect-error - TS2322 - Type '{ refetch: (newVariables: any) => Promise<TVariables["response"] | undefined>; data: unknown; error: Error | undefined; loading: boolean; } | { loading: boolean; data: null; error: undefined; refetch: () => Promise<...>; }' is not assignable to type 'QueryResult<TVariables, TData>'.
	return result;
};
